<div [hidden]="$isDoing3DAuth | async">
  <div class="token-generation container">
    <h2 class="token-generation-title title">
      {{ 'panTokenGeneration.title' | translate }}
    </h2>
    <div class="pan-tokenization-tabs">
      <button
        type="button"
        class="pan-tokenization-tab"
        [class.active]="($activeTab | async) === 'PAN'"
        (click)="switchTab('PAN')"
      >
        {{ 'panTokenGeneration.tabTitleForPan' | translate }}
      </button>
      <button
        type="button"
        class="pan-tokenization-tab"
        [class.active]="($activeTab | async) === 'PRN'"
        (click)="switchTab('PRN')"
      >
        {{ 'panTokenGeneration.tabTitleForPrn' | translate }}
      </button>
    </div>
    <form [formGroup]="form" class="token-generation-form form-container">
      <!-- Card Number Field -->
      <div class="form-field" [ngClass]="{ 'field-invalid': showPanError }" *ngIf="($activeTab | async) === 'PAN'">
        <label for="pan" class="form-label">
          {{ 'application.cardNumber' | translate }}
        </label>
        <input id="pan" type="text" formControlName="pan" class="form-input" appPanFormatter
               [useSpaces]="($panFormatting | async) ?? true" [attr.placeholder]="($placeholders | async) ? (($panFormatting | async) !== false ? '1234 5678 9123 4567' : '1234567891234567') : null" />
      </div>
      <!-- Payment Reference Number Field -->
      <div class="form-field" [ngClass]="{ 'field-invalid': showPrnError }" *ngIf="($activeTab | async) === 'PRN'">
        <label for="prn" class="form-label">
          {{ 'application.paymentReferenceNumber' | translate }}
        </label>
        <input id="prn" type="text" formControlName="prn" class="form-input" inputmode="numeric" pattern="\d*" maxlength="11" />
      </div>

      @if (($activeTab | async) === 'PAN') {

        @if ($showExtendedFields | async) {
          <!-- Expiration Date Fields -->
          <div class="form-field" [ngClass]="{ 'field-invalid': showExpirationError }">
            <!-- eslint-disable-next-line -->
            <label class="form-label">
              {{ 'panTokenGeneration.expDate' | translate }}
            </label>
            <div class="expiration-fields">
              <input id="expDateMonth" type="text" maxlength="2" formControlName="expDateMonth"
                     class="form-input form-input-exp form-input-exp-month" #expDateMonthInput appAutoFocusTransition
                     [next]="expDateYearInput" [attr.placeholder]="($placeholders | async) ? '09' : null" />
              <span class="expiration-separator">/</span>
              <input id="expDateYear" type="text" maxlength="2" formControlName="expDateYear"
                     class="form-input form-input-exp form-input-exp-year" #expDateYearInput appAutoFocusTransition
                     [prev]="expDateMonthInput" [attr.placeholder]="($placeholders | async) ? '28' : null" />
            </div>
          </div>
        }

        @if ($showNameField | async) {
          <!-- Card Owner Name Field -->
          <div class="form-field" [ngClass]="{ 'field-invalid': showNameError }">
            <label for="cardOwnerName" class="form-label">
              {{ 'panTokenGeneration.cardOwnerName' | translate }}
            </label>
            <input id="cardOwnerName" type="text" formControlName="cardOwnerName" class="form-input" appNameSanitizer [attr.placeholder]="($placeholders | async) ? 'John Doe' : null" />
          </div>
        }
      }
    
        <!-- Submit Button -->
        <button
          type="button"
          [disabled]="($permissiveSubmitButton | async) ? ($isProcessedOrProcessing | async) : ($isProcessedOrProcessing | async) || form.invalid"
          (click)="onSubmit()"
          class="form-button"
        >
          {{ 'panTokenGeneration.generate' | translate }}
        </button>
    </form>

    <!-- Unified Error Messages -->
    <div class="form-errors">
      @if (($activeTab | async) === 'PAN') {
        @if (showPanError && fc.pan.missing) {
          <div class="error-message">
            {{ 'panTokenGeneration.cardNumberRequired' | translate }}
          </div>
        } @else if (showPanError && fc.pan.tooShort) {
          <div class="error-message">
            {{ 'panTokenGeneration.cardNumberTooShort' | translate }}
          </div>
        } @else if (showPanError && fc.pan.tooLong) {
          <div class="error-message">
            {{ 'panTokenGeneration.cardNumberTooLong' | translate }}
          </div>
        } @else if (showPanError && fc.pan.luhnInvalid) {
          <div class="error-message">
            {{ 'panTokenGeneration.wrongCardNumber' | translate }}
          </div>
        }

        @if (showExpirationError && fc.expDateMonth.missing) {
          <div class="error-message">
            {{ 'panTokenGeneration.expDateMonthRequired' | translate }}
          </div>
        } @else if (showExpirationError && fc.expDateMonth.badFormat) {
          <div class="error-message">
            {{ 'panTokenGeneration.wrongExpMonth' | translate }}
          </div>
        }

        @if (showExpirationError && fc.expDateYear.missing) {
          <div class="error-message">
            {{ 'panTokenGeneration.expDateYearRequired' | translate }}
          </div>
        } @else if (showExpirationError && fc.expDateYear.patternFail) {
          <div class="error-message">
            {{ 'panTokenGeneration.wrongExpYear' | translate }}
          </div>
        }

        @if (showExpirationError && expirationDateInPast) {
          <div class="error-message">
            {{ 'panTokenGeneration.expInPast' | translate }}
          </div>
        }

        @if (showNameError && fc.cardOwnerName.missing) {
          <div class="error-message">
            {{ 'panTokenGeneration.cardOwnerNameRequired' | translate }}
          </div>
        }
      }

      @if (($activeTab | async) === 'PRN') {
        @if (showPrnError && fc.prn.missing) {
          <div class="error-message">
            {{ 'panTokenGeneration.prnRequired' | translate }}
          </div>
        } @else if (showPrnError && fc.prn.patternFail) {
          <div class="error-message">
            {{ 'panTokenGeneration.prnInvalidFormat' | translate }}
          </div>
        } @else if (showPrnError && fc.prn.luhnInvalid) {
          <div class="error-message">
            {{ 'panTokenGeneration.wrongPrn' | translate }}
          </div>
        }
      }

      @for (error of ($tpsErrors | async); track error) {
        <div class="error-message">
          {{ 'error.somethingWentWrong' | translate }}: {{ 'ecommerceResponseCodes.' + error.responseCodeName | translate }}
        </div>
      }
    </div>
  </div>
</div>
<iframe class="hostApp-3DSecure" [hidden]="($isDoing3DAuth | async) === false" id="3DSecureFrame" width="100%"
        height="100%"></iframe>
